import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import {
  getCustomerUserId,
  isLoggedInCustomer,
  loadYardProfile,
  saveYardProfile,
  YARD_GOAL_CHIPS,
} from '../utils/customerAccount';

export default function YardProfilePage() {
  const navigate = useNavigate();
  const userId = getCustomerUserId();
  const [cityRegion, setCityRegion] = useState('');
  const [goalId, setGoalId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Your yard profile | PlantaDay';
  }, []);

  useEffect(() => {
    if (!isLoggedInCustomer()) {
      navigate('/login', { replace: true, state: { returnTo: '/yard-profile' } });
      return;
    }
    const p = loadYardProfile(userId);
    if (p) {
      setCityRegion(p.cityRegion || '');
      setGoalId(p.goalId || '');
    }
  }, [navigate, userId]);

  const handleSave = (e) => {
    e.preventDefault();
    if (!userId) return;
    setSaving(true);
    saveYardProfile(userId, {
      cityRegion: cityRegion.trim(),
      goalId: goalId || null,
    });
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('plantaday_show_yard_profile');
    }
    setSaving(false);
    navigate('/ideas');
  };

  const handleSkip = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('plantaday_show_yard_profile');
    }
    navigate('/ideas');
  };

  if (!isLoggedInCustomer()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={userId} />
      <main className="flex-1 max-w-lg mx-auto px-4 py-10 sm:py-14 w-full">
        <h1 className="text-2xl font-bold text-gray-900">Tell us about your yard</h1>
        <p className="mt-2 text-gray-600 text-sm leading-relaxed">
          We will use this to personalize your AI answers without you having to explain every time.
        </p>

        <form onSubmit={handleSave} className="mt-8 space-y-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div>
            <label htmlFor="yard-city-region" className="block text-sm font-medium text-gray-700 mb-1">
              Your city or region
            </label>
            <input
              id="yard-city-region"
              type="text"
              value={cityRegion}
              onChange={(e) => setCityRegion(e.target.value)}
              placeholder="e.g. Victoria BC"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">Main yard goal</p>
            <p className="text-xs text-gray-500 mb-3">Pick one.</p>
            <div className="flex flex-wrap gap-2">
              {YARD_GOAL_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setGoalId(chip.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    goalId === chip.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-gray-50 text-gray-800 border-gray-200 hover:border-green-400'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              Save my yard profile
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
            >
              Skip for now
            </button>
          </div>
        </form>

        <p className="mt-6 text-center">
          <Link to="/ideas" className="text-sm text-gray-600 hover:text-green-700 hover:underline">
            Back to yard ideas
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
